import React, { useEffect, useRef, useState, useCallback } from 'react'
import Spinner from './Spinner'
import { FilterAdjustments, filterUtils } from '@renderer/utils/filterUtils'
import { getCachedLUT } from '@renderer/utils/lutUtils'
import { computeBitmapCropFromSensor } from '@renderer/utils/previewLayout'
import {
  publishSharedPreviewCapture,
  peekSharedPreviewCapture,
  type PreviewCaptureShareGroupId
} from '@renderer/utils/previewCaptureShare'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import Stats from 'stats.js'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import FilterWorker from '@renderer/workers/filter.worker.ts?worker'
import { imageUtils } from '@renderer/utils/imageUtils'
import {
  saveOriginalSettings,
  applyLiveViewBrightness,
  restoreOriginalSettings,
  type OriginalSettings
} from '@renderer/utils/cameras/cameraSettingsManager'

interface PreviewOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number
}

type CameraKey = 'primary' | 'secondary'

interface PreviewProps {
  capturePreview?: (cameraKey: CameraKey, options?: PreviewOptions) => Promise<Blob | undefined>
  adjustments?: FilterAdjustments
  filter?: string // Used as a key for re-renders
  postLUTAdjustments?: { vignette?: number; grain?: number; blur?: number; clarity?: number; sharpeningAmount?: number }
  ratio?: number
  cameraRatio?: number
  previewQuality?: PreviewOptions
  onCanvasReady?: (dimensions: { width: number; height: number }) => void
  /** 2D canvas with previewRotation + mirror baked into pixels for WebCodecs recorders */
  onRecordingCanvasReady?: (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    dimensions: { width: number; height: number }
  ) => void
  cameraKey?: CameraKey
  isPaused?: boolean
  fillContainer?: boolean // If true, fill container (object-cover behavior) instead of centering
  /** Producer captures from camera; consumer reuses the producer's blob for this group (same cameraKey). */
  captureShareRole?: 'producer' | 'consumer'
  captureShareGroupId?: PreviewCaptureShareGroupId
}

const Preview: React.FC<PreviewProps> = ({
  capturePreview = unifiedCameraUtils.capturePreview,
  ratio,
  cameraRatio,
  adjustments,
  filter,
  postLUTAdjustments,
  onCanvasReady,
  onRecordingCanvasReady,
  cameraKey = useSessionStateStore().userPreferences.selectedCamera as 'primary' | 'secondary',
  isPaused = false,
  fillContainer = false,
  captureShareRole,
  captureShareGroupId
}) => {
  const { cameraSettings, secondaryCameraSettings, debugFps, selectedCamera } = useSettingsStore()
  const settings = cameraKey === 'primary' ? cameraSettings : secondaryCameraSettings

  // Add minimal validation for settings (do not default captureRatio to 4:3 — that re-enables
  // L2 crop when the setting is missing; same semantics as getNormalizedCaptureRatio().)
  const rawSettingsCaptureRatio = settings?.captureRatio
  const mirror = settings?.mirror ?? true
  const applyFilterOnPreview = settings?.applyFilterOnPreview ?? true
  const previewQuality = settings?.previewQuality ?? {
    maxWidth: 1280,
    maxHeight: 720,
    quality: 0.8,
    resizeQuality: 'medium'
  }
  const rotation = settings?.previewRotation ?? 0

  const normalizedCaptureRatio =
    rawSettingsCaptureRatio === 'auto'
      ? undefined
      : typeof rawSettingsCaptureRatio === 'number' &&
          Number.isFinite(rawSettingsCaptureRatio) &&
          rawSettingsCaptureRatio > 0
        ? rawSettingsCaptureRatio
        : undefined

  const effectiveCameraRatio = cameraRatio ?? normalizedCaptureRatio

  const [isCanvasLoaded, setIsCanvasLoaded] = useState(false)
  const isWorkerReadyRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  /** Latest onCanvasReady for video encoder setup — ref avoids stale closures in worker/rAF paths. */
  const onCanvasReadyRef = useRef<PreviewProps['onCanvasReady']>(onCanvasReady)
  onCanvasReadyRef.current = onCanvasReady
  const onRecordingCanvasReadyRef = useRef<PreviewProps['onRecordingCanvasReady']>(onRecordingCanvasReady)
  onRecordingCanvasReadyRef.current = onRecordingCanvasReady
  /** Draw filtered/cropped bitmap → recording buffer (avoids readback from display canvas). */
  const syncRecordingFromBitmapRef = useRef<((bmp: ImageBitmap) => void) | null>(null)
  const lastPreviewFrameTimeRef = useRef(0)
  const lastLutWorkerFilterRef = useRef<string>('')
  const recordingCanvasRef = useRef<HTMLCanvasElement>(null)
  const lastReportedRecordingDimsRef = useRef<{ width: number; height: number } | null>(null)
  /** Skip redundant parent updates when backing-store size is unchanged. */
  const lastReportedCanvasDimsRef = useRef<{ width: number; height: number } | null>(null)
  /**
   * Sensor aspect ratio (originalWidth / originalHeight) discovered in the last getBitmapParam()
   * call. Used for layout sizing when effectiveCameraRatio is undefined ('auto') so the preview
   * box matches the real camera feed instead of falling back to a hardcoded 4:3.
   */
  const sensorAspectRef = useRef<number | undefined>(undefined)
  /** Set inside preview effect so getBitmapParam can re-run layout when sensor aspect is first known (auto ratio). */
  const updateCanvasSizeRef = useRef<(() => void) | null>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number>()
  const throttledRef = useRef<number>(0)
  const isUnmountedRef = useRef(false)
  const isWorkerBusyRef = useRef(false)
  const [error, setError] = useState<string>()
  const statsRef = useRef<Stats | null>(debugFps ? new Stats() : null)
  const filterWorkerRef = useRef<Worker>()
  const currentCameraKeyRef = useRef<CameraKey>(cameraKey)
  const originalSettingsRef = useRef<OriginalSettings | null>(null)
  const isLiveViewBrightnessAppliedRef = useRef<boolean>(false)

  // Setup worker
  useEffect(() => {
    filterWorkerRef.current = new FilterWorker()
    lastLutWorkerFilterRef.current = ''
    const canvas = canvasRef.current
    const canvasCtx = canvas?.getContext('bitmaprenderer')

    const worker = filterWorkerRef.current
    worker.onmessage = (e: MessageEvent): void => {
      isWorkerReadyRef.current = true

      if (e.data.filteredBitmap) {
        const filteredBitmap = e.data.filteredBitmap as ImageBitmap
        if (canvasCtx) {
          syncRecordingFromBitmapRef.current?.(filteredBitmap)
          canvasCtx.transferFromImageBitmap(filteredBitmap)
          const cb = onCanvasReadyRef.current
          const el = canvasRef.current
          if (cb && el && el.width > 0 && el.height > 0) {
            const w = el.width
            const h = el.height
            const last = lastReportedCanvasDimsRef.current
            if (!last || last.width !== w || last.height !== h) {
              lastReportedCanvasDimsRef.current = { width: w, height: h }
              cb({ width: w, height: h })
            }
          }
        } else {
          filteredBitmap.close()
        }
      }
      isWorkerBusyRef.current = false
    }

    return (): void => {
      worker.terminate()
    }
  }, [])

  // This effect will reset isWorkerReady whenever the filter adjustments change.
  // This ensures the new filter is applied instantly on the main thread
  // before the worker takes over again.
  useEffect(() => {
    isWorkerReadyRef.current = false
    lastLutWorkerFilterRef.current = ''
  }, [filter])

  const getBitmapParam = useCallback(async (): Promise<
    | {
        sx: number
        sy: number
        cropWidth: number
        cropHeight: number
        originalWidth: number
        originalHeight: number
      }
    | undefined
  > => {
    try {
      let originalWidth: number
      let originalHeight: number

      if (captureShareRole === 'consumer' && captureShareGroupId) {
        const shared = peekSharedPreviewCapture(captureShareGroupId)
        if (!shared || shared.cameraKey !== cameraKey || isUnmountedRef.current) return undefined
        originalWidth = shared.originalWidth
        originalHeight = shared.originalHeight
      } else {
        const blob = await capturePreview(cameraKey, {
          ...previewQuality,
          rotation: 0,
          mirror: false
        })
        if (!blob || isUnmountedRef.current) return undefined

        const img = await createImageBitmap(blob)
        originalWidth = img.width
        originalHeight = img.height

        if (originalWidth <= 0 || originalHeight <= 0) {
          img.close()
          console.warn('Invalid image dimensions:', originalWidth, originalHeight)
          return undefined
        }
        img.close()

        if (captureShareRole === 'producer' && captureShareGroupId) {
          publishSharedPreviewCapture(captureShareGroupId, cameraKey, blob, originalWidth, originalHeight)
        }
      }

      const newSensor = originalWidth / originalHeight
      const prevSensor = sensorAspectRef.current
      sensorAspectRef.current = newSensor
      if (
        effectiveCameraRatio === undefined &&
        (prevSensor === undefined || Math.abs(prevSensor - newSensor) > 0.005)
      ) {
        updateCanvasSizeRef.current?.()
      }

      const { sx, sy, cropWidth, cropHeight } = computeBitmapCropFromSensor(
        originalWidth,
        originalHeight,
        effectiveCameraRatio,
        fillContainer,
        ratio
      )

      return {
        sx,
        sy,
        cropWidth,
        cropHeight,
        originalWidth,
        originalHeight
      }
    } catch (error) {
      console.error('Error in getBitmapParam:', error)
      return undefined
    }
  }, [
    capturePreview,
    effectiveCameraRatio,
    previewQuality,
    cameraKey,
    fillContainer,
    ratio,
    captureShareRole,
    captureShareGroupId
  ])

  /** Report canvas backing-store size for WebCodecs recorders (must match encoder width/height). */
  const reportPreviewCanvasDimensions = useCallback((): void => {
    const cb = onCanvasReadyRef.current
    if (!cb) return
    const el = canvasRef.current
    if (!el || el.width <= 0 || el.height <= 0) return
    const w = el.width
    const h = el.height
    const last = lastReportedCanvasDimsRef.current
    if (last && last.width === w && last.height === h) return
    lastReportedCanvasDimsRef.current = { width: w, height: h }
    cb({ width: w, height: h })
  }, [])

  useEffect(() => {
    // Initialize stats if debug mode is on
    if (debugFps && statsRef.current && previewRef.current) {
      previewRef.current.appendChild(statsRef.current.dom)
    }

    return (): void => {
      if (statsRef.current?.dom && previewRef.current?.contains(statsRef.current.dom)) {
        previewRef.current.removeChild(statsRef.current.dom)
      }
    }
  }, [debugFps])

  useEffect(() => {
    // When paused, stop preview refresh loop
    if (isPaused) {
      isUnmountedRef.current = true
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = undefined
      }
      return
    }

    isUnmountedRef.current = false
    if (!capturePreview) return

    const container = containerRef.current
    const preview = previewRef.current
    const canvas = canvasRef.current
    const overlay = overlayRef.current
    if (!container || !preview || !canvas || !overlay) return

    // Get canvas context for bitmap rendering
    const canvasCtx = canvas.getContext('bitmaprenderer')
    if (!canvasCtx) return

    /**
     * Recording buffer matches the filtered preview bitmap (same pixels as display), with
     * rotation/mirror baked for WebCodecs — avoids readback via createImageBitmap(display).
     */
    const syncRecordingFromBitmap = (bmp: ImageBitmap): void => {
      const rec = recordingCanvasRef.current
      const cbRec = onRecordingCanvasReadyRef.current
      if (!rec || !cbRec || bmp.width <= 0 || bmp.height <= 0) return

      const norm = ((rotation % 360) + 360) % 360
      const isRot90or270 = norm === 90 || norm === 270
      const rawRecW = isRot90or270 ? bmp.height : bmp.width
      const rawRecH = isRot90or270 ? bmp.width : bmp.height
      const roundedRecW = Math.max(2, Math.round(rawRecW / 2) * 2)
      const roundedRecH = Math.max(2, Math.round(rawRecH / 2) * 2)

      if (rec.width !== roundedRecW || rec.height !== roundedRecH) {
        rec.width = roundedRecW
        rec.height = roundedRecH
        const rctxFresh = rec.getContext('2d')
        if (rctxFresh) {
          const lastR = lastReportedRecordingDimsRef.current
          if (!lastR || lastR.width !== roundedRecW || lastR.height !== roundedRecH) {
            lastReportedRecordingDimsRef.current = { width: roundedRecW, height: roundedRecH }
            cbRec(rec, rctxFresh, { width: roundedRecW, height: roundedRecH })
          }
        }
      }

      const recCtx = rec.getContext('2d')
      if (!recCtx) return

      const w = bmp.width
      const h = bmp.height
      const rw = rec.width
      const rh = rec.height
      recCtx.setTransform(1, 0, 0, 1, 0, 0)
      recCtx.clearRect(0, 0, rw, rh)
      recCtx.translate(rw / 2, rh / 2)
      if (norm === 0 || norm === 180) {
        if (mirror) recCtx.scale(-1, 1)
        if (norm !== 0) recCtx.rotate((norm * Math.PI) / 180)
      } else {
        recCtx.rotate((norm * Math.PI) / 180)
        if (mirror) recCtx.scale(1, -1)
      }
      recCtx.drawImage(bmp, -w / 2, -h / 2)
    }
    syncRecordingFromBitmapRef.current = syncRecordingFromBitmap

    // Reset loading and error state when camera changes
    // Check if camera actually changed
    const cameraChanged = currentCameraKeyRef.current !== cameraKey
    if (cameraChanged) {
      currentCameraKeyRef.current = cameraKey
      lastReportedCanvasDimsRef.current = null
      lastReportedRecordingDimsRef.current = null
      sensorAspectRef.current = undefined // reset so stale sensor dims don't pollute new camera layout
      lastLutWorkerFilterRef.current = ''
      setIsCanvasLoaded(false)
      setError(undefined)
    }

    let bitmapParams: Awaited<ReturnType<typeof getBitmapParam>>

    const updateCanvasSize = (): void => {
      if (throttledRef.current) {
        cancelAnimationFrame(throttledRef.current)
      }

      throttledRef.current = requestAnimationFrame(() => {
        throttledRef.current = 0

        const containerWidth = container.offsetWidth
        const containerHeight = container.offsetHeight

        // ── Rotation-aware layout ────────────────────────────────────────────────
        // A CSS rotate(90°/270°) on the canvas element changes its *visual* footprint
        // (width↔height swap) but does NOT change its layout box.  Stretching the
        // canvas to '100%' of its (un-rotated) parent would squeeze/stretch the image.
        //
        // Correct approach:
        //  1. Compute the previewDiv size using the *post-rotation* aspect ratio so it
        //     occupies the right amount of screen space.
        //  2. Keep the canvas at its *pre-rotation* natural pixel size (previewHeight × previewWidth
        //     for 90°/270°) with explicit px values — no '100%' stretching.
        //  3. Center the canvas inside previewDiv absolutely, then apply the CSS rotate.
        //     After rotation the canvas visual footprint = previewDiv size exactly.
        // ────────────────────────────────────────────────────────────────────────

        const isRotated90or270 = rotation === 90 || rotation === 270

        // The aspect ratio *as it will appear on screen* after CSS rotation.
        // For 90/270°, width and height swap, so the ratio inverts.
        const displayCameraRatio =
          effectiveCameraRatio !== undefined && isRotated90or270
            ? 1 / effectiveCameraRatio
            : effectiveCameraRatio

        // Strip placeholders: L3 bitmap aspect matches slot `ratio`; cover-layout must use that
        // (not only camera crop aspect) or the inner box is sized wrong and the feed looks shrunk.
        const slotFillRatio =
          fillContainer &&
          ratio !== undefined &&
          typeof ratio === 'number' &&
          Number.isFinite(ratio) &&
          ratio > 0
            ? isRotated90or270
              ? 1 / ratio
              : ratio
            : undefined
        const coverSourceRatio = slotFillRatio ?? displayCameraRatio

        // Step 1: size the previewDiv to fit the rotated visual footprint within the container.
        let previewWidth: number, previewHeight: number
        if (fillContainer) {
          if (coverSourceRatio === undefined) {
            previewWidth = containerWidth
            previewHeight = containerHeight
          } else {
            const scaleBasedOnWidth = containerWidth / coverSourceRatio
            const scaleBasedOnHeight = containerHeight
            const scale = Math.max(scaleBasedOnWidth, scaleBasedOnHeight)
            previewWidth = scale * coverSourceRatio
            previewHeight = scale
          }
        } else if (displayCameraRatio === undefined) {
          // 'auto' — no explicit crop ratio set. Use the real sensor aspect if available;
          // fall back to the container ratio (i.e. fill without stretching) until the first
          // camera frame arrives.  Never hardcode 4:3 — that forces a crop box on any sensor
          // that isn't exactly 4:3 (e.g. 16:9 webcams, 3:2 DSLRs).
          const fallbackRatio = sensorAspectRef.current ?? (containerWidth / containerHeight)
          const isRotatedForLayout = isRotated90or270 // already computed above
          const displayFallback = isRotatedForLayout ? 1 / fallbackRatio : fallbackRatio
          if (displayFallback >= 1) {
            previewWidth = Math.min(containerWidth, containerHeight * displayFallback)
            previewHeight = previewWidth / displayFallback
          } else {
            previewHeight = Math.min(containerHeight, containerWidth / displayFallback)
            previewWidth = previewHeight * displayFallback
          }
        } else if (displayCameraRatio >= 1) {
          previewWidth = Math.min(containerWidth, containerHeight * displayCameraRatio)
          previewHeight = previewWidth / displayCameraRatio
        } else {
          previewHeight = Math.min(containerHeight, containerWidth / displayCameraRatio)
          previewWidth = previewHeight * displayCameraRatio
        }

        // Set previewDiv size and center it in the container.
        preview.style.width = `${previewWidth}px`
        preview.style.height = `${previewHeight}px`
        preview.style.left = `${(containerWidth - previewWidth) / 2}px`
        preview.style.top = `${(containerHeight - previewHeight) / 2}px`

        // Step 2: set the canvas pixel buffer and CSS display size.
        // For 0°/180°: canvas display size = previewDiv size (no rotation swap needed).
        // For 90°/270°: the canvas will be physically rotated 90°, so its pre-rotation
        //   natural display size must be (previewHeight × previewWidth) — after the CSS
        //   rotate these become (previewWidth × previewHeight) on screen, matching previewDiv.
        let canvasDisplayW: number, canvasDisplayH: number
        if (isRotated90or270) {
          canvasDisplayW = previewHeight  // canvas "width" before rotation = target height after
          canvasDisplayH = previewWidth   // canvas "height" before rotation = target width after
        } else {
          canvasDisplayW = previewWidth
          canvasDisplayH = previewHeight
        }

        // Pixel buffer matches the display size (1:1 logical pixels, no extra scaling).
        canvas.width = Math.round(canvasDisplayW)
        canvas.height = Math.round(canvasDisplayH)

        // Recording canvas dimensions are driven in syncRecordingFromDisplay from the real
        // camera bitmap size (after transferFromImageBitmap), not layout — avoids stale rec size.

        // Pin the canvas to an explicit pixel size — never use '100%' after rotation because
        // that stretches the canvas to fill its (un-rotated) parent box.
        canvas.style.width = `${canvasDisplayW}px`
        canvas.style.height = `${canvasDisplayH}px`

        // Step 3: center the canvas absolutely inside previewDiv, then rotate.
        // After rotate(90deg), the visual footprint becomes canvasDisplayH × canvasDisplayW
        // = previewWidth × previewHeight, which exactly fills previewDiv.
        canvas.style.position = 'absolute'
        canvas.style.top = '50%'
        canvas.style.left = '50%'

        // Build the full transform: translate-to-center → rotate → mirror.
        // getRotationAwareMirrorTransform returns scaleX(-1) or scaleY(-1) to produce a
        // correct visual horizontal flip given the current CSS rotation angle.
        const mirrorTransform = imageUtils.getRotationAwareMirrorTransform(rotation || 0, mirror)
        const translateTransform = 'translate(-50%, -50%)'
        const rotateTransform = rotation ? `rotate(${rotation}deg)` : ''
        canvas.style.transform = [translateTransform, rotateTransform, mirrorTransform]
          .filter(Boolean)
          .join(' ')
        canvas.style.transformOrigin = 'center center'

        // Calculate frame capture area dimensions (overlay mask).
        // Use the screen-space (post-rotation) previewDiv dimensions as the reference.
        const overlayWidth = fillContainer ? containerWidth : previewWidth
        const overlayHeight = fillContainer ? containerHeight : previewHeight

        // L3 highlight: only when parent passes explicit `ratio` (frame slot / display box).
        // Never use store captureRatio here — that caused phantom masks in SettingsModal (no frame).
        const visibleCameraRatio =
          displayCameraRatio !== undefined
            ? displayCameraRatio
            : sensorAspectRef.current !== undefined
              ? isRotated90or270
                ? 1 / sensorAspectRef.current
                : sensorAspectRef.current
              : undefined

        const overlayRatio =
          ratio === undefined ? undefined : isRotated90or270 ? 1 / ratio : ratio

        let captureWidth = 0
        let captureHeight = 0
        if (overlayRatio !== undefined) {
          if (overlayRatio >= 1) {
            captureWidth = Math.min(overlayWidth, overlayHeight * overlayRatio)
            captureHeight = captureWidth / overlayRatio
          } else {
            captureHeight = Math.min(overlayHeight, overlayWidth / overlayRatio)
            captureWidth = captureHeight * overlayRatio
          }
        }

        // Only show frame overlay when explicit ratio differs from visible camera (screen-space).
        if (
          !fillContainer &&
          overlayRatio !== undefined &&
          visibleCameraRatio !== undefined &&
          Math.abs(overlayRatio - visibleCameraRatio) > 0.01
        ) {
          const captureLeft = (overlayWidth - captureWidth) / 2
          const captureTop = (overlayHeight - captureHeight) / 2

          overlay.innerHTML = `
            <svg width="${overlayWidth}" height="${overlayHeight}" style="position: absolute; inset: 0;">
              <defs>
                <mask id="mask">
                  <rect width="100%" height="100%" fill="white"/>
                  <rect x="${captureLeft}" y="${captureTop}" width="${captureWidth}" height="${captureHeight}" fill="black"/>
                </mask>
              </defs>
              <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#mask)"/>
            </svg>
          `
        } else {
          overlay.innerHTML = ''
        }

        reportPreviewCanvasDimensions()
      })
    }

    updateCanvasSizeRef.current = updateCanvasSize

    const resizeObserver = new ResizeObserver(() => {
      updateCanvasSize()
    })
    resizeObserver.observe(container)
    updateCanvasSize()

    const refreshPreview = async (retryCount = 0): Promise<void> => {
      if (isUnmountedRef.current || isPaused) return

      try {
        const previewMaxFps = settings.previewQuality?.maxFps
        if (
          previewMaxFps != null &&
          previewMaxFps > 0 &&
          !onRecordingCanvasReadyRef.current
        ) {
          const minMs = 1000 / previewMaxFps
          const now = performance.now()
          if (now - lastPreviewFrameTimeRef.current < minMs) {
            if (!isUnmountedRef.current && !isPaused) {
              animationRef.current = requestAnimationFrame(refreshPreview)
            }
            return
          }
          lastPreviewFrameTimeRef.current = now
        }

        // Reset bitmapParams if camera changed (bitmapParams is in closure but we need fresh one)
        if (!bitmapParams) {
          bitmapParams = await getBitmapParam()
          if (!bitmapParams) {
            // Retry after a short delay if camera might not be ready yet
            if (retryCount < 5) {
              await new Promise((resolve) => setTimeout(resolve, 100 * (retryCount + 1)))
              return refreshPreview(retryCount + 1)
            }
            setError('Camera not ready. Please try switching cameras or refreshing.')
            return
          }
        }

        let blob: Blob | undefined
        if (captureShareRole === 'consumer' && captureShareGroupId) {
          const shared = peekSharedPreviewCapture(captureShareGroupId)
          if (!shared?.blob || shared.cameraKey !== cameraKey) {
            if (retryCount < 5) {
              await new Promise((resolve) => setTimeout(resolve, 100 * (retryCount + 1)))
              return refreshPreview(retryCount + 1)
            }
            setError('Failed to capture from camera')
            return
          }
          blob = shared.blob
        } else {
          blob = await capturePreview(cameraKey, {
            ...previewQuality,
            rotation: 0,
            mirror: false
          })
        }

        if (!blob) {
          if (retryCount < 5) {
            await new Promise((resolve) => setTimeout(resolve, 100 * (retryCount + 1)))
            return refreshPreview(retryCount + 1)
          }
          setError('Failed to capture from camera')
          return
        }
        if (isUnmountedRef.current || isPaused) return

        if (captureShareRole === 'producer' && captureShareGroupId) {
          publishSharedPreviewCapture(
            captureShareGroupId,
            cameraKey,
            blob,
            bitmapParams.originalWidth,
            bitmapParams.originalHeight
          )
        }

        const { sx, sy, cropWidth, cropHeight } = bitmapParams

        const cropAspect = cropWidth / cropHeight
        let cssW = canvas.width
        let cssH = canvas.height
        const displayAspect = (): number =>
          cssW > 0 && cssH > 0 ? cssW / cssH : cropAspect
        if (Math.abs(cropAspect - displayAspect()) > 0.01) {
          updateCanvasSizeRef.current?.()
          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                resolve()
              })
            })
          })
          cssW = canvas.width
          cssH = canvas.height
        }

        // Fit the crop region inside previewQuality's max box while preserving its aspect ratio.
        // Forcing resize to maxWidth×maxHeight would stretch non-matching-aspect crops (e.g. a
        // 4:3 crop into the default 1280×720 box), leaving the canvas backing store with the
        // wrong aspect and poisoning every downstream recorder.
        const maxW = settings.previewQuality?.maxWidth ?? 1280
        const maxH = settings.previewQuality?.maxHeight ?? 720
        const fitScale = Math.min(maxW / cropWidth, maxH / cropHeight, 1)
        const targetW = Math.max(2, Math.round((cropWidth * fitScale) / 2) * 2)
        const targetH = Math.max(2, Math.round((cropHeight * fitScale) / 2) * 2)

        const croppedImg = await createImageBitmap(blob, sx, sy, cropWidth, cropHeight, {
          resizeQuality: settings.previewQuality?.resizeQuality || 'medium',
          resizeWidth: targetW,
          resizeHeight: targetH
        })

        if (applyFilterOnPreview && (adjustments || (filter && getCachedLUT(filter)))) {
          const lutData = filter ? getCachedLUT(filter) : null
          if (lutData) {
            const worker = filterWorkerRef.current
            if (filter && lastLutWorkerFilterRef.current !== filter) {
              const data = new Float32Array(lutData.data)
              worker?.postMessage(
                {
                  type: 'lutInit',
                  lutData: { size: lutData.size, data, title: lutData.title }
                },
                { transfer: [data.buffer] }
              )
              lastLutWorkerFilterRef.current = filter
            }
            if (!isWorkerBusyRef.current && worker) {
              isWorkerBusyRef.current = true
              worker.postMessage(
                {
                  type: 'lutFrame',
                  bitmap: croppedImg,
                  postLUTAdjustments
                },
                [croppedImg]
              )
            } else {
              croppedImg.close()
            }
          } else if (adjustments) {
            if (!isWorkerReadyRef.current) {
              const offscreenCanvas = new OffscreenCanvas(croppedImg.width, croppedImg.height)
              const offscreenCtx = offscreenCanvas.getContext('2d', { willReadFrequently: true })

              if (offscreenCtx) {
                offscreenCtx.drawImage(croppedImg, 0, 0)
                filterUtils.applyAdjustments(offscreenCtx, adjustments)
                const filteredBitmap = offscreenCanvas.transferToImageBitmap()
                syncRecordingFromBitmapRef.current?.(filteredBitmap)
                canvasCtx?.transferFromImageBitmap(filteredBitmap)
              }
              if (!isWorkerBusyRef.current) {
                isWorkerBusyRef.current = true
                filterWorkerRef.current?.postMessage({ bitmap: croppedImg, adjustments }, [
                  croppedImg
                ])
              } else {
                croppedImg.close()
              }
            } else {
              if (!isWorkerBusyRef.current) {
                isWorkerBusyRef.current = true
                filterWorkerRef.current?.postMessage({ bitmap: croppedImg, adjustments }, [
                  croppedImg
                ])
              } else {
                croppedImg.close()
              }
            }
          }
        } else {
          syncRecordingFromBitmapRef.current?.(croppedImg)
          canvasCtx?.transferFromImageBitmap(croppedImg)
        }

        reportPreviewCanvasDimensions()
        setIsCanvasLoaded(true)
      } catch (err) {
        console.error('Preview refresh failed:', err)
        setError('Failed to load camera preview')
        setIsCanvasLoaded(false)
      }

      if (!isUnmountedRef.current && !isPaused) {
        animationRef.current = requestAnimationFrame(refreshPreview)
        statsRef.current?.update()
      }
    }

    // Initialize and start preview with a small delay to ensure camera is ready
    const initializePreview = async (): Promise<void> => {
      // Small delay to allow camera connection to stabilize after switching
      await new Promise((resolve) => setTimeout(resolve, 150))
      if (isUnmountedRef.current || isPaused) return

      // Handle live view brightness feature
      const shouldApplyBrightness =
        settings?.autoBrightenLiveViewForFlash && settings?.useShutterCapture

      if (!shouldApplyBrightness && isLiveViewBrightnessAppliedRef.current) {
        // Restore original settings if toggle was disabled
        try {
          if (originalSettingsRef.current) {
            await restoreOriginalSettings(cameraKey, originalSettingsRef.current)
            originalSettingsRef.current = null
            isLiveViewBrightnessAppliedRef.current = false
            console.log('[Preview] Restored original settings (toggle disabled)')
          }
        } catch (error) {
          console.error('[Preview] Error restoring original settings:', error)
        }
      }

      bitmapParams = await getBitmapParam()
      if (bitmapParams) {
        // Only attempt live-view brightness adjustment after preview is confirmed responsive.
        // This avoids hammering config APIs while the camera is still initializing/busy.
        if (shouldApplyBrightness && !isLiveViewBrightnessAppliedRef.current) {
          try {
            // Save original settings first
            const original = await saveOriginalSettings(cameraKey)
            if (original) {
              originalSettingsRef.current = original

              // Apply live view brightness settings
              const applied = await applyLiveViewBrightness(cameraKey, {
                autoBrightenLiveViewForFlash: settings.autoBrightenLiveViewForFlash,
                liveViewShutterSpeedOverride: settings.liveViewShutterSpeedOverride
              })

              if (applied) {
                isLiveViewBrightnessAppliedRef.current = true
                console.log('[Preview] Applied live view brightness settings')
              } else {
                console.warn('[Preview] Failed to apply live view brightness settings')
                // Note: We don't show toast here to avoid spamming during preview initialization
                // The UI already shows a warning if the camera doesn't support the feature
              }
            }
          } catch (error) {
            console.error('[Preview] Error applying live view brightness:', error)
          }
        }

        refreshPreview()
      } else {
        // Retry initialization if camera not ready
        setTimeout(() => {
          if (!isUnmountedRef.current && !isPaused) {
            initializePreview()
          }
        }, 200)
      }
    }

    initializePreview()

    return (): void => {
      isUnmountedRef.current = true
      updateCanvasSizeRef.current = null
      syncRecordingFromBitmapRef.current = null
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      resizeObserver.disconnect()

      // Restore original settings on unmount if they were applied
      if (isLiveViewBrightnessAppliedRef.current && originalSettingsRef.current) {
        restoreOriginalSettings(cameraKey, originalSettingsRef.current).catch((error) => {
          console.error('[Preview] Error restoring settings on unmount:', error)
        })
        originalSettingsRef.current = null
        isLiveViewBrightnessAppliedRef.current = false
      }
    }
  }, [
    capturePreview,
    effectiveCameraRatio,
    ratio,
    getBitmapParam,
    mirror,
    reportPreviewCanvasDimensions,
    adjustments,
    filter,
    postLUTAdjustments,
    applyFilterOnPreview,
    cameraKey,
    previewQuality,
    settings?.previewQuality,
    settings?.autoBrightenLiveViewForFlash,
    settings?.liveViewShutterSpeedOverride,
    settings?.useShutterCapture,
    rotation,
    isPaused,
    selectedCamera,
    fillContainer,
    settings,
    captureShareRole,
    captureShareGroupId
  ])

  return (
    <div ref={containerRef} className={`w-full h-full relative overflow-hidden ${fillContainer ? '' : 'bg-black'}`}>
      {!capturePreview ? (
        <div className="absolute inset-0 flex items-center justify-center text-white">
          Please select a camera
        </div>
      ) : (
        <div ref={previewRef} className="absolute" style={{ overflow: 'hidden' }}>
          <canvas ref={canvasRef} className="absolute" data-preview-type="camera" />
          <canvas
            ref={recordingCanvasRef}
            aria-hidden
            data-preview-type="camera-recording"
            className="absolute pointer-events-none"
            style={{ width: 0, height: 0, opacity: 0, position: 'fixed', left: -9999, top: 0 }}
          />
          <div ref={overlayRef} className="absolute inset-0" />
        </div>
      )}
      {!isCanvasLoaded && (
        <div className="absolute inset-0 z-[1] flex flex-col gap-2 justify-center items-center text-white">
          {error ? (
            <span>{error}</span>
          ) : (
            <>
              <Spinner />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default Preview
